import { z } from 'zod';

// Single source of truth for trafficking platform URN identifiers. Used both
// to build the zod enums and as runtime constants by callers (localStorage
// dispatcher, route loaders, etc.). If a value changes here, every consumer
// updates automatically.
export const TRAFFICKING_PLATFORM_URN = {
  adform: 'urn:mint:value:platform_v2:adform',
  appsflyer: 'urn:mint:value:platform_v2:appsflyer',
  campaignManager: 'urn:mint:value:platform_v2:campaign_manager',
  googleAnalytics: 'urn:mint:value:platform_v2:google_analytics',
} as const;

export const AdserverUrnEnum = z.enum([
  TRAFFICKING_PLATFORM_URN.adform,
  TRAFFICKING_PLATFORM_URN.appsflyer,
  TRAFFICKING_PLATFORM_URN.campaignManager,
]);

export type AdserverUrnType = z.infer<typeof AdserverUrnEnum>;

export const TraffickingPlatformUrnEnum = z.enum([
  ...AdserverUrnEnum.options,
  TRAFFICKING_PLATFORM_URN.googleAnalytics,
]);

export type TraffickingPlatformUrnType = z.infer<
  typeof TraffickingPlatformUrnEnum
>;

export const OmniTraffickingIntegrationResponseSchema = z.object({
  data: z
    .object({
      message: z.string().nullable(),
      items: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
            companyId: z.number(),
            isActive: z.boolean().prefault(true),
            adserver: AdserverUrnEnum,
            extra: z.object({
              appsflyerIosId: z.string().nullable(),
              appsflyerAndroidId: z.string().nullable(),
              adformAdvertiser: z
                .object({
                  id: z.string(),
                  name: z.string(),
                })
                .nullable(),
              campaignManagerAdvertisers: z
                .array(
                  z.object({
                    id: z.string(),
                    name: z.string(),
                    floodlightConfigId: z.string(),
                  }),
                )
                .nullable(),
              campaignManagerProfile: z
                .object({
                  id: z.string(),
                  name: z.string(),
                })
                .nullable(),
              campaignManagerIsPaidSearch: z.boolean().nullable(),
              campaignManagerIsFloodlightEligible: z.boolean().nullable(),
              campaignManagerIsDv360Linked: z.boolean().nullable(),
            }),
            // Back-end returns "user_id" and "user_updated_at" for audit info
            userUpdatedAt: z.string().nullish(), //.nullable(),
            userId: z.string().nullish(), //.nullable(),
            usingInAccounts: z.boolean().nullish(), //.nullable(),
            expired: z.boolean().nullish(), //.nullable(),
          }),
        )
        .nullable(),
    })
    .nullable(),
  error: z
    .object({
      type: z.string(),
      message: z.string(),
      validationErrors: z
        .array(
          z.object({
            loc: z.array(z.string()),
            msg: z.string(),
            type: z.string(),
          }),
        )
        .nullable(),
      code: z.number(),
    })
    .nullable(),
});

export type OmniTraffickingIntegrationsResponse = z.infer<
  typeof OmniTraffickingIntegrationResponseSchema
>;

export type OmniTraffickingIntegration = NonNullable<
  NonNullable<OmniTraffickingIntegrationsResponse['data']>['items']
>[number];
